import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styled from 'styled-components';
import API_URL from '../config';
import {
  runChecks,
  countBySeverity,
  groupByLesson,
  issuesToText,
  findCandidateGaps,
  SEVERITY_META,
  SEVERITY_ORDER,
} from './lessonChecks';

// ─── Конфигурация разделов ──────────────────────────────────────────────────
// Эндпоинты уже существуют на бэкенде (text.controller.ts):
//   POST /api/text/save/shabbat-school        (ss)
//   POST /api/text/save/youth-shabbat-school  (yss)
//   POST /api/text/save/morning-reading       (mr)
//   POST /api/text/save/evening-reading       (er)
// Поле формы — всегда `file` (FileInterceptor('file')).
//
// Новое: разбор PDF (POST /api/text/parse-pdf/:type) и правки после разбора
// (POST /api/text/apply-corrections) — через pdfparser (внутренний сервис).
// ER пока не откалиброван для PDF (нет extract_pdf_er.py) — pdfParseType
// не задан, вкладка ER в PDF-режиме недоступна.

const SECTIONS = [
  { key: 'ss',  label: 'Субботняя школа',          endpoint: '/api/text/save/shabbat-school',       prefix: 'SS',  pdfParseType: 'ss'  },
  { key: 'yss', label: 'Молодёжная суб. школа',     endpoint: '/api/text/save/youth-shabbat-school', prefix: 'YSS', pdfParseType: 'yss' },
  { key: 'mr',  label: 'Утренний страж',            endpoint: '/api/text/save/morning-reading',      prefix: 'MR',  pdfParseType: 'mr'  },
  { key: 'er',  label: 'Вечерний страж',            endpoint: '/api/text/save/evening-reading',      prefix: 'ER',  pdfParseType: null  },
];

// ─── Книга -> число (для формы ручной правки) ──────────────────────────────
// Тот же список, что и BOOK_NAMES ниже, но как массив пар для <select>.

// ─── Стили блоков предпросмотра (зеркалят рендер мобильного приложения) ───────
// См. golosnadezhdi_2.0/.../convert-data-to-component.tsx

const BLOCK_KIND = {
  // заголовки
  'bibleVersesTitle': 'title',
  'memoryVerseTitle': 'title',
  'subTitle':         'title',
  'sub-title':        'title',
  'section':          'section',
  // обычный текст / стихи для чтения
  'mainText':         'main',
  'meinText':         'main', // опечатка в данных MR/ER — оставлена намеренно
  'pray':             'main',
  'verseBible':       'main',
  'AdditionalVerses': 'main',
  'mainVerses':       'main',
  // выделенные блоки
  'memoryVerse':      'verse',
  'question':         'question',
  'lists':            'list',
};

const KIND_META = {
  title:    { label: 'Заголовок',      color: '#0f172a' },
  section:  { label: 'Раздел',         color: '#7c3aed' },
  main:     { label: 'Основной текст', color: '#334155' },
  verse:    { label: 'Памятный стих',  color: '#047857' },
  question: { label: 'Вопрос',         color: '#047857' },
  list:     { label: 'Список',         color: '#047857' },
  unknown:  { label: 'Неизвестный',    color: '#dc2626' },
};

const LINK_COLOR = '#1d4ed8';

// ─── Книги Библии: числовой код → [сокращение, полное название] ───────────────
// Коды совпадают с booksNameNumber в Servise_gn/src/Service.ts.
const BOOK_NAMES = {
  10: ['Быт', 'Бытие'], 20: ['Исх', 'Исход'], 30: ['Лев', 'Левит'], 40: ['Числ', 'Числа'],
  50: ['Втор', 'Второзаконие'], 60: ['Нав', 'Иисус Навин'], 70: ['Суд', 'Книга Судей'], 80: ['Руф', 'Руфь'],
  90: ['1 Цар', '1 Царств'], 100: ['2 Цар', '2 Царств'], 110: ['3 Цар', '3 Царств'], 120: ['4 Цар', '4 Царств'],
  130: ['1 Пар', '1 Паралипоменон'], 140: ['2 Пар', '2 Паралипоменон'], 150: ['Ездр', 'Ездра'], 160: ['Неем', 'Неемия'],
  190: ['Есф', 'Есфирь'], 220: ['Иов', 'Иов'], 230: ['Пс', 'Псалтирь'], 240: ['Притч', 'Притчи'],
  250: ['Еккл', 'Екклесиаст'], 260: ['Песн', 'Песнь Песней'], 290: ['Ис', 'Исаия'], 300: ['Иер', 'Иеремия'],
  310: ['Плач', 'Плач Иеремии'], 330: ['Иез', 'Иезекииль'], 340: ['Дан', 'Даниил'], 350: ['Ос', 'Осия'],
  360: ['Иоил', 'Иоиль'], 370: ['Амос', 'Амос'], 380: ['Авд', 'Авдий'], 390: ['Ион', 'Иона'],
  400: ['Мих', 'Михей'], 410: ['Наум', 'Наум'], 420: ['Авв', 'Аввакум'], 430: ['Соф', 'Софония'],
  440: ['Агг', 'Аггей'], 450: ['Зах', 'Захария'], 460: ['Мал', 'Малахия'], 470: ['Мф', 'Евангелие от Матфея'],
  480: ['Мк', 'Евангелие от Марка'], 490: ['Лк', 'Евангелие от Луки'], 500: ['Ин', 'Евангелие от Иоанна'],
  510: ['Деян', 'Деяния'], 520: ['Рим', 'Римлянам'], 530: ['1 Кор', '1 Коринфянам'], 540: ['2 Кор', '2 Коринфянам'],
  550: ['Гал', 'Галатам'], 560: ['Еф', 'Ефесянам'], 570: ['Флп', 'Филиппийцам'], 580: ['Кол', 'Колоссянам'],
  590: ['1 Фес', '1 Фессалоникийцам'], 600: ['2 Фес', '2 Фессалоникийцам'], 610: ['1 Тим', '1 Тимофею'],
  620: ['2 Тим', '2 Тимофею'], 630: ['Тит', 'Титу'], 640: ['Флм', 'Филимону'], 650: ['Евр', 'Евреям'],
  660: ['Иак', 'Иакова'], 670: ['1 Петр', '1 Петра'], 680: ['2 Петр', '2 Петра'], 690: ['1 Ин', '1 Иоанна'],
  700: ['2 Ин', '2 Иоанна'], 710: ['3 Ин', '3 Иоанна'], 720: ['Иуд', 'Иуды'], 730: ['Откр', 'Откровение'],
};

function bookLabel(num) {
  const b = BOOK_NAMES[num];
  return b ? `${b[0]} (${b[1]})` : `книга ${num ?? '—'}`;
}

// Отсортированный список книг для <select> в форме ручной правки.
const BOOK_OPTIONS = Object.entries(BOOK_NAMES).map(([num, [abbr, full]]) => ({
  value: Number(num),
  label: `${abbr} — ${full}`,
}));

// Поиск нераспознанных кандидатов на ссылку (findCandidateGaps) переехал в
// lessonChecks.js: та же функция подсвечивает их в тексте предпросмотра и
// перечисляет в панели автопроверок — чтобы не разошлись две копии одной
// эвристики.

// ─── Styled ───────────────────────────────────────────────────────────────────

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04);
  margin-bottom: 1.5rem;
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Tab = styled.button`
  padding: 0.55rem 1rem;
  border-radius: 9px;
  border: 1px solid ${p => p.active ? '#4f46e5' : '#e2e8f0'};
  background: ${p => p.active ? '#4f46e5' : '#fff'};
  color: ${p => p.active ? '#fff' : '#475569'};
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #4f46e5; }
`;

const FormatGuide = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #4f46e5;
  border-radius: 8px;
  padding: 1rem 1.1rem;
  margin-bottom: 1.5rem;
`;

const GuideTitle = styled.p`
  margin: 0 0 0.5rem;
  font-size: 12px;
  font-weight: 700;
  color: #4f46e5;
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

const GuideNote = styled.p`
  margin: 0.2rem 0 0;
  font-size: 12.5px;
  color: #64748b;
`;

const DropArea = styled.div`
  border: 2px dashed ${p => p.isDragActive ? '#4f46e5' : '#cbd5e1'};
  border-radius: 12px;
  background: ${p => p.isDragActive ? '#eef2ff' : '#f8fafc'};
  padding: 2.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: #4f46e5; background: #eef2ff; }
`;

const DropIcon = styled.div`
  width: 44px; height: 44px;
  border-radius: 10px;
  background: #e0e7ff;
  display: flex; align-items: center; justify-content: center;
  svg { color: #4f46e5; }
`;

const DropText = styled.p`
  margin: 0; font-size: 14px; color: #475569; text-align: center;
  strong { color: #4f46e5; }
`;

const DropHint = styled.p` margin: 0; font-size: 12px; color: #94a3b8; `;

const ErrorMsg = styled.p` font-size: 13px; color: #ef4444; margin: 1rem 0 0; `;

// Сводка
const Summary = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.75rem;
  margin: 1.25rem 0;
`;

const Stat = styled.div`
  flex: 1; min-width: 130px;
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 0.75rem 1rem;
  span { display: block; font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
  strong { font-size: 17px; color: #0f172a; }
`;

const FileBar = styled.div`
  display: flex; align-items: center; gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 9px;
  margin-top: 1rem;
  span { flex: 1; font-size: 13px; font-weight: 500; color: #166534; word-break: break-all; }
`;

const Legend = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.5rem 1rem;
  padding: 0.75rem 1rem;
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 9px;
  margin: 1rem 0;
  font-size: 12px; color: #475569;
`;

const LegendDot = styled.span`
  display: inline-flex; align-items: center; gap: 0.35rem;
  &::before {
    content: ''; width: 10px; height: 10px; border-radius: 3px;
    background: ${p => p.color};
  }
`;

// Навигатор уроков
const NavBar = styled.div`
  display: flex; align-items: center; gap: 0.75rem;
  margin: 1rem 0;
  flex-wrap: wrap;
`;

const NavBtn = styled.button`
  padding: 0.5rem 0.9rem;
  border: 1px solid #e2e8f0; border-radius: 8px;
  background: #fff; color: #475569; font-size: 13px; font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${p => p.disabled ? 0.5 : 1};
  &:hover:not(:disabled) { border-color: #4f46e5; color: #4f46e5; }
`;

const LessonSelect = styled.select`
  flex: 1; min-width: 200px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0; border-radius: 8px;
  font-size: 13px; color: #334155; background: #fff; cursor: pointer;
`;

// Лист предпросмотра (как страница урока)
// Лист предпросмотра и панель автопроверок стоят рядом; на узком экране
// панель уезжает под лист.
const PreviewLayout = styled.div`
  display: grid;
  grid-template-columns: ${p => (p.$withPanel ? 'minmax(0, 1fr) 340px' : 'minmax(0, 1fr)')};
  gap: 1rem;
  align-items: start;
  @media (max-width: 1000px) { grid-template-columns: minmax(0, 1fr); }
`;

// Панель проблем. Липкая: при прокрутке страницы остаётся на виду, иначе на
// длинном уроке она уезжает вверх и снова «пропадает». На узком экране колонка
// схлопывается — тогда панель встаёт НАД листом (order: -1), а не под ним:
// под листом высотой в экран её никто не находит, из-за чего и казалось, что
// автопроверок в предпросмотре нет вовсе.
const Panel = styled.div`
  display: flex; flex-direction: column;
  border: 1px solid #e2e8f0; border-radius: 12px;
  background: #fff;
  position: sticky;
  top: 116px;
  /* align-items: start у грида ужал бы колонку по содержимому — тогда
     липнуть было бы негде и sticky ничего не делал бы. */
  align-self: stretch;
  max-height: max(360px, calc(100vh - 200px));

  @media (max-width: 1000px) {
    order: -1;
    position: static;
    max-height: 300px;
  }
`;

const PanelHead = styled.div`
  padding: 0.75rem 0.9rem;
  border-bottom: 1px solid #f1f5f9;
`;

const PanelTitle = styled.p`
  margin: 0; font-size: 12px; font-weight: 700; color: #0f172a;
  text-transform: uppercase; letter-spacing: 0.06em;
`;

const ChipRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.35rem;
  margin-top: 0.6rem;
`;

const Chip = styled.button`
  display: inline-flex; align-items: center; gap: 0.3rem;
  padding: 0.25rem 0.5rem;
  border-radius: 7px;
  border: 1px solid ${p => (p.$on ? p.$color : '#e2e8f0')};
  background: ${p => (p.$on ? `${p.$color}14` : '#fff')};
  color: ${p => (p.$on ? p.$color : '#64748b')};
  font-size: 11.5px; font-weight: 600; cursor: pointer;
  &:hover { border-color: ${p => p.$color}; }
  &::before {
    content: ''; width: 7px; height: 7px; border-radius: 50%;
    background: ${p => p.$color};
  }
`;

const IssueList = styled.div`
  flex: 1; overflow-y: auto;
`;

const IssueRow = styled.button`
  display: block; width: 100%; text-align: left;
  border: none; border-left: 3px solid ${p => p.$color};
  border-bottom: 1px solid #f1f5f9;
  background: ${p => (p.$active ? '#eef2ff' : '#fff')};
  padding: 0.55rem 0.8rem;
  cursor: pointer;
  &:hover { background: ${p => (p.$active ? '#eef2ff' : '#f8fafc')}; }
`;

const IssueWhere = styled.div`
  font-size: 10.5px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em;
`;

const IssueMsg = styled.div`
  font-size: 12.5px; color: #0f172a; font-weight: 600; line-height: 1.4;
  margin: 0.15rem 0;
`;

const IssueExcerpt = styled.div`
  font-size: 11.5px; color: #64748b; font-style: italic; line-height: 1.4;
  word-break: break-word;
`;

const PanelFoot = styled.div`
  display: flex; gap: 0.4rem; flex-wrap: wrap;
  padding: 0.6rem 0.8rem;
  border-top: 1px solid #f1f5f9;
`;

// Заголовок группы в сквозном списке проблем: список идёт по ВСЕМ урокам
// сразу, иначе при 91 уроке пришлось бы открывать каждый вручную.
const IssueGroup = styled.div`
  position: sticky; top: 0; z-index: 1;
  padding: 0.35rem 0.8rem;
  background: #f8fafc; border-bottom: 1px solid #e2e8f0;
  font-size: 11px; font-weight: 700; color: #475569;
  text-transform: uppercase; letter-spacing: 0.04em;
`;

const PanelOk = styled.div`
  padding: 2rem 1rem; text-align: center;
  font-size: 13px; color: #16a34a; font-weight: 600;
`;

// Тон строки состояния — по самой тяжёлой из найденных проблем.
const BAR_TONE = {
  error: { bg: '#fef2f2', border: '#fecaca', color: '#991b1b' },
  warn:  { bg: '#fffbeb', border: '#fde68a', color: '#92400e' },
  info:  { bg: '#f0f9ff', border: '#bae6fd', color: '#075985' },
  ok:    { bg: '#f0fdf4', border: '#bbf7d0', color: '#166534' },
};

// Строка состояния автопроверок — единственное место, которое видно всегда,
// независимо от ширины окна и от того, свёрнута панель или нет. Липнет под
// шапку админки (её высота — 56px, см. TopBar в App.js).
const IssueBar = styled.div`
  position: sticky;
  top: 56px;
  z-index: 20;
  display: flex; align-items: center; flex-wrap: wrap; gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${p => p.$tone.border};
  background: ${p => p.$tone.bg};
  border-radius: 10px;
`;

const BarTitle = styled.span`
  font-size: 13px; font-weight: 700; color: ${p => p.$tone.color};
`;

const BarNote = styled.span`
  font-size: 12px; color: #64748b;
`;

const BarSpacer = styled.span` flex: 1; `;

const BarBtn = styled.button`
  padding: 0.3rem 0.6rem;
  border: 1px solid #e2e8f0; border-radius: 7px;
  background: #fff; color: #475569;
  font-size: 12px; font-weight: 600;
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  &:hover:not(:disabled) { border-color: #4f46e5; color: #4f46e5; }
`;

const GateBar = styled.label`
  display: flex; align-items: center; gap: 0.5rem;
  margin-top: 1rem; padding: 0.7rem 0.9rem;
  background: #fef2f2; border: 1px solid #fecaca; border-radius: 9px;
  font-size: 12.5px; color: #991b1b; cursor: pointer;
`;

const PreviewSheet = styled.div`
  position: relative; /* offsetParent для прокрутки к блоку из панели */
  border: 1px solid #e2e8f0; border-radius: 12px;
  background: #fbfcfd;
  padding: 1.5rem 1.75rem;
  /* Раньше был фиксированный max-height: 600px — на большом экране под
     панелью пустовало место впустую. Теперь высота растёт вместе с окном
     (за вычетом шапки/вкладок/сводки/легенды над блоком и кнопки отправки
     под ним — прикидка), но не проваливается ниже 360px на низких экранах. */
  max-height: max(360px, calc(100vh - 420px));
  overflow-y: auto;
`;

const LessonHead = styled.div`
  margin-bottom: 1.25rem; padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
  h3 { margin: 0 0 0.35rem; font-size: 1.15rem; color: #0f172a; }
  p  { margin: 0; font-size: 12.5px; color: #94a3b8; }
`;

const Block = styled.div`
  margin-bottom: 0.875rem;
  line-height: 1.6;
  font-size: 15px;
  white-space: pre-wrap; /* чтобы \n в тексте переносил строки, как в приложении */

  ${p => p.kind === 'title' && `
    font-size: 1.05rem; font-weight: 700; color: #0f172a;
    margin-top: 0.5rem;
  `}
  ${p => p.kind === 'section' && `
    font-size: 1rem; font-weight: 700; color: #7c3aed;
    padding: 0.5rem 0.75rem; background: #f5f3ff; border-radius: 8px;
    display: inline-block;
  `}
  ${p => p.kind === 'main' && `
    color: #334155;
    background: #f1f5f9; border-radius: 10px; padding: 0.75rem 0.9rem;
  `}
  ${p => p.kind === 'verse' && `
    color: #065f46; font-weight: 600;
    background: #ecfdf5; border-left: 3px solid #10b981;
    border-radius: 8px; padding: 0.75rem 0.9rem;
  `}
  ${p => p.kind === 'question' && `
    color: #065f46;
    background: #ecfdf5; border-left: 3px solid #10b981;
    border-radius: 8px; padding: 0.75rem 0.9rem;
  `}
  ${p => p.kind === 'list' && `
    color: #065f46;
    background: #f0fdf4; border-radius: 8px; padding: 0.625rem 0.9rem;
  `}
  ${p => p.kind === 'unknown' && `
    color: #991b1b;
    background: #fef2f2; border: 1px dashed #fca5a5;
    border-radius: 8px; padding: 0.625rem 0.9rem;
  `}

  /* блок, к которому перешли из панели автопроверок */
  ${p => p.$active && `
    outline: 2px solid #f59e0b;
    outline-offset: 4px;
    border-radius: 8px;
  `}
`;

const TypeTag = styled.span`
  display: inline-block;
  font-size: 10px; font-weight: 700; letter-spacing: 0.03em;
  color: ${p => p.color}; background: ${p => p.color}1a;
  padding: 0.05rem 0.4rem; border-radius: 5px;
  margin-right: 0.5rem; vertical-align: middle;
  text-transform: uppercase;
`;

const LinkWrap = styled.span`
  position: relative;
  display: inline;
`;

// Жёлтая подсветка «похоже на ссылку, но не распознано». Кликабельна: по
// нажатию выбирается соответствующая строка в панели проверок и открывается
// меню действий (отметить как ссылку / сообщить). Раньше это была немая
// подсказка, и с найденным фрагментом нельзя было ничего сделать, не выделяя
// его заново мышью.
const GapMark = styled.span`
  background: #fef9c3;
  border-bottom: 1px dashed #ca8a04;
  border-radius: 3px;
  cursor: pointer;
  padding: 0 1px;
  &:hover { background: #fde68a; }
  ${p => p.$active && `
    background: #f59e0b;
    box-shadow: 0 0 0 2px #b45309;
  `}
`;

// Меню действий над жёлтым фрагментом.
const GapMenu = styled.div`
  display: flex; gap: 0.35rem;
  background: #0f172a;
  border-radius: 9px;
  padding: 0.25rem;
  box-shadow: 0 6px 16px rgba(0,0,0,0.25);
`;

const CorrectionBadge = styled.span`
  position: absolute;
  top: -8px; right: -6px;
  background: #4f46e5; color: #fff;
  font-size: 10px; font-weight: 700;
  border-radius: 999px;
  padding: 0.1rem 0.4rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

// Плавающая кнопка «Отметить как ссылку» рядом с выделением текста.
const SelectionToolbar = styled.div`
  position: fixed;
  z-index: 80;
  top: ${p => p.$top}px;
  left: ${p => p.$left}px;
  transform: translate(-50%, -100%);
`;

const SelectBtn = styled.button`
  padding: 0.4rem 0.7rem;
  border: none; border-radius: 8px;
  background: #0f172a; color: #fff;
  font-size: 12.5px; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  box-shadow: 0 6px 16px rgba(0,0,0,0.25);
  &:hover { background: #1e293b; }
`;

const CorrModal = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 90;
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
`;

const CorrCard = styled.div`
  background: #fff; border-radius: 14px;
  padding: 1.5rem; width: 100%; max-width: 420px;
  box-shadow: 0 20px 50px rgba(0,0,0,0.25);
`;

const CorrTitle = styled.h4` margin: 0 0 0.75rem; font-size: 15px; color: #0f172a; `;

const CorrFragment = styled.div`
  background: #f1f5f9; border-radius: 8px;
  padding: 0.6rem 0.8rem; margin-bottom: 1rem;
  font-size: 13.5px; color: #334155; font-weight: 600;
`;

const ModeRow = styled.div`
  display: flex; gap: 0.5rem; margin-bottom: 1rem;
`;

const ModeBtn = styled.button`
  flex: 1; padding: 0.5rem; border-radius: 8px;
  border: 1px solid ${p => p.active ? '#4f46e5' : '#e2e8f0'};
  background: ${p => p.active ? '#eef2ff' : '#fff'};
  color: ${p => p.active ? '#4f46e5' : '#64748b'};
  font-size: 12.5px; font-weight: 600; cursor: pointer;
`;

const FieldLabel = styled.label`
  display: block; font-size: 11.5px; font-weight: 600;
  color: #64748b; margin-bottom: 0.3rem;
`;

const FieldRow = styled.div` margin-bottom: 0.85rem; `;

const TextInput = styled.input`
  width: 100%; padding: 0.5rem 0.65rem;
  border: 1px solid #e2e8f0; border-radius: 8px;
  font-size: 13.5px; box-sizing: border-box;
`;

const Select = styled.select`
  width: 100%; padding: 0.5rem 0.65rem;
  border: 1px solid #e2e8f0; border-radius: 8px;
  font-size: 13.5px; background: #fff; box-sizing: border-box;
`;

// Один отрывок = одна строка формы. Их может быть несколько: «Рим. 15:4, 16:22»
// — это два разных места Писания в одном подсвеченном фрагменте.
const RefCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  padding: 0.7rem 0.75rem 0.15rem;
  margin-bottom: 0.6rem;
  background: #f8fafc;
`;

const RefHead = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 11px; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.05em;
`;

const RefRemove = styled.button`
  border: none; background: none; color: #94a3b8;
  font-size: 15px; line-height: 1; cursor: pointer; padding: 0 0.2rem;
  &:hover { color: #dc2626; }
`;

const AddRefBtn = styled.button`
  width: 100%;
  padding: 0.5rem;
  border: 1px dashed #cbd5e1; border-radius: 9px;
  background: #fff; color: #4f46e5;
  font-size: 12.5px; font-weight: 600; cursor: pointer;
  margin-bottom: 0.5rem;
  &:hover { border-color: #4f46e5; background: #f5f3ff; }
`;

const RefPreview = styled.div`
  margin: 0.1rem 0 0.75rem;
  font-size: 11.5px; color: #64748b; line-height: 1.5;
  b { color: #0f172a; font-weight: 600; }
`;

const RefWarn = styled.span`
  display: block; margin-top: 0.35rem;
  color: #b45309; font-weight: 600; line-height: 1.45;
`;

const SplitRefBtn = styled.button`
  margin-top: 0.35rem;
  padding: 0.3rem 0.6rem;
  border: 1px solid #f59e0b; border-radius: 7px;
  background: #fffbeb; color: #92400e;
  font-size: 11.5px; font-weight: 700; cursor: pointer;
  &:hover { background: #fef3c7; }
`;

const CorrActions = styled.div`
  display: flex; gap: 0.6rem; margin-top: 1.2rem;
`;

const GhostBtn = styled.button`
  flex: 1; padding: 0.6rem; border-radius: 8px;
  border: 1px solid #e2e8f0; background: #fff; color: #64748b;
  font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover { border-color: #cbd5e1; }
`;

const PrimaryBtn = styled.button`
  flex: 1; padding: 0.6rem; border-radius: 8px;
  border: none; background: #4f46e5; color: #fff;
  font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover { background: #4338ca; }
  &:disabled { background: #e2e8f0; color: #94a3b8; cursor: not-allowed; }
`;

const CorrectionsBar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.75rem; margin: 1rem 0;
  padding: 0.75rem 1rem;
  background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 9px;
  font-size: 13px; color: #4338ca; font-weight: 600;
`;

const ReapplyBtn = styled.button`
  padding: 0.5rem 1rem; border: none; border-radius: 8px;
  background: #4f46e5; color: #fff; font-size: 13px; font-weight: 700;
  cursor: pointer;
  &:hover { background: #4338ca; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const PdfParamsBox = styled.div`
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 1.1rem 1.25rem; margin-bottom: 1rem;
`;

const InlineForm = styled.div`
  display: flex; gap: 0.75rem; align-items: flex-end; flex-wrap: wrap;
`;

const ModeSwitchRow = styled.div`
  display: flex; justify-content: flex-end; margin-bottom: 0.75rem;
`;

const LinkBtn = styled.button`
  border: none; background: none; color: #4f46e5;
  font-size: 12.5px; font-weight: 600; cursor: pointer;
  padding: 0.2rem 0;
  &:hover { text-decoration: underline; }
`;

const ReportBtn = styled.button`
  border: none; background: none; color: #94a3b8;
  font-size: 11px; cursor: pointer; padding: 0.1rem 0.3rem;
  &:hover { color: #dc2626; }
`;

const BibleLink = styled.span`
  color: ${LINK_COLOR};
  font-weight: 700;
  border-bottom: 1px dotted ${LINK_COLOR};
  cursor: pointer;
  border-radius: 3px;
  ${p => p.$open && `background: ${LINK_COLOR}1a;`}
  ${p => p.$active && `background: #fde68a; box-shadow: 0 0 0 2px #b45309;`}
  &:hover { background: ${LINK_COLOR}14; }
`;

const Popover = styled.div`
  position: absolute;
  z-index: 60;
  top: calc(100% + 6px);
  left: 0;
  min-width: 240px;
  max-width: 340px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.18);
  padding: 0.55rem 0.7rem 0.65rem;
  font-size: 12.5px;
  font-weight: 400;
  color: #334155;
  cursor: default;
  text-align: left;
  white-space: normal;
`;

const PopHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;
  font-weight: 700; color: #0f172a; font-size: 13px;
  margin-bottom: 0.45rem; padding-bottom: 0.35rem;
  border-bottom: 1px solid #f1f5f9;
`;

const PopClose = styled.button`
  border: none; background: none; padding: 0; line-height: 1;
  font-size: 16px; color: #94a3b8; cursor: pointer;
  &:hover { color: #475569; }
`;

const RefRow = styled.div`
  & + & { margin-top: 0.45rem; padding-top: 0.45rem; border-top: 1px dashed #eef2f7; }
`;

const RefField = styled.div`
  display: flex; gap: 0.4rem; line-height: 1.5;
  b { color: #94a3b8; font-weight: 600; min-width: 52px; flex-shrink: 0; }
`;

const PopHint = styled.div`
  margin-top: 0.5rem; padding-top: 0.4rem; border-top: 1px solid #f1f5f9;
  font-size: 11px; color: #94a3b8; font-style: italic;
`;

const QHint = styled.span`
  display: block; margin-top: 0.4rem;
  font-size: 11px; color: #94a3b8; font-style: italic;
`;

const SubmitBtn = styled.button`
  width: 100%;
  margin-top: 1.25rem;
  padding: 0.75rem 1.5rem;
  border: none; border-radius: 9px;
  background: ${p => p.disabled ? '#e2e8f0' : '#4f46e5'};
  color: ${p => p.disabled ? '#94a3b8' : '#fff'};
  font-size: 14.5px; font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.15s, transform 0.1s;
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  &:hover:not(:disabled) { background: #4338ca; transform: translateY(-1px); }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function detectSectionFromName(name) {
  const upper = name.toUpperCase();
  // YSS должен проверяться раньше SS, т.к. содержит "SS"
  if (/^YSS/.test(upper)) return 'yss';
  if (/^SS/.test(upper))  return 'ss';
  if (/^MR/.test(upper))  return 'mr';
  if (/^ER/.test(upper))  return 'er';
  // Русские названия файлов (МСШ, Молодёжная суб. школа, Утренний/Вечерний страж и т.п.).
  // Молодёжную проверяем раньше обычной субботней школы, т.к. в её названии тоже есть "суббот".
  if (/МСШ|МОЛОД[ЕЁ]Ж/.test(upper)) return 'yss';
  if (/ССШ|СУББОТН/.test(upper))    return 'ss';
  if (/УТРЕНН/.test(upper))         return 'mr';
  if (/ВЕЧЕРН/.test(upper))         return 'er';
  return null;
}

// Краткое описание для нативной подсказки при наведении.
function describeLink(link) {
  if (!link?.data?.length) return link?.text || '';
  return link.data
    .map(d => `${bookLabel(d.bookNumber)}: гл. ${(d.chapter || []).join(', ') || '—'}, ст. ${(d.verses || []).join(', ') || 'вся глава'}`)
    .join(' · ');
}

// Содержит ли ссылка межглавный диапазон (в тексте есть "глава:стих–глава:стих").
function isCrossChapter(link) {
  return /\d+\s*:\s*\d+\s*[–-]\s*\d+\s*:\s*\d+/.test(link?.text || '');
}

// Ссылка с popover по клику, раскрывающим её содержимое (разобранные книга/глава/стихи).
function LinkChip({ link, active }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const data = Array.isArray(link?.data) ? link.data : [];

  return (
    <LinkWrap ref={wrapRef}>
      <BibleLink
        $open={open}
        $active={active}
        data-fragment={link.text}
        title={describeLink(link)}
        onClick={() => setOpen(o => !o)}
      >
        {link.text}
      </BibleLink>
      {open && (
        <Popover onClick={(e) => e.stopPropagation()}>
          <PopHead>
            <span>{link.text}</span>
            <PopClose type="button" aria-label="Закрыть" onClick={() => setOpen(false)}>×</PopClose>
          </PopHead>

          {data.length === 0 && (
            <div style={{ color: '#94a3b8' }}>Нет разобранных данных в ссылке.</div>
          )}

          {data.map((d, i) => {
            const chapters = d.chapter || [];
            const verses = d.verses || [];
            return (
              <RefRow key={i}>
                <RefField><b>Книга:</b><span>{bookLabel(d.bookNumber)} · код {d.bookNumber ?? '—'}</span></RefField>
                <RefField><b>{chapters.length > 1 ? 'Главы:' : 'Глава:'}</b><span>{chapters.join(', ') || '—'}</span></RefField>
                <RefField><b>Стихи:</b><span>{verses.length ? verses.join(', ') : 'вся глава'}</span></RefField>
              </RefRow>
            );
          })}

          {isCrossChapter(link) && (
            <PopHint>Межглавный диапазон: в тексте показан полностью, в данных хранится начальный стих.</PopHint>
          )}
        </Popover>
      )}
    </LinkWrap>
  );
}

// Разбивает text на части по link.text (повторяет splitTextByLink из приложения)
// и возвращает массив React-узлов с подсвеченными ссылками. Второй проход —
// по оставшемуся простому тексту — подсвечивает жёлтым «похоже на ссылку, но
// не распознано» (см. findCandidateGaps), чтобы было видно, куда смотреть.
function renderTextWithLinks(text, links, opts = {}) {
  const { activeFragment = null, onGapClick = null } = opts;
  let parts = [text || ''];
  // Сначала длинные тексты ссылок, потом короткие: если у одного блока есть
  // и "Быт. 3" (короче, например уже устаревшая/неверно распознанная), и
  // "Быт. 3:9" (длиннее, правильная — например только что добавленная
  // правкой), обработка по порядку массива могла бы дать короткой ссылке
  // "съесть" префикс раньше, чем длинная успеет найти свою полную подстроку —
  // тогда правка выглядела бы так, будто ничего не изменилось. Сортировка по
  // убыванию длины исключает этот сценарий.
  const orderedLinks = [...(links || [])].sort((a, b) => (b?.text?.length || 0) - (a?.text?.length || 0));
  orderedLinks.forEach((link) => {
    if (!link?.text) return;
    const next = [];
    parts.forEach((part) => {
      if (typeof part !== 'string') { next.push(part); return; }
      const chunks = part.split(link.text);
      chunks.forEach((chunk, i) => {
        next.push(chunk);
        if (i < chunks.length - 1) next.push({ __link: link });
      });
    });
    parts = next;
  });

  const withGaps = [];
  parts.forEach((part) => {
    if (typeof part !== 'string') { withGaps.push(part); return; }
    // Уже вырезанные выше настоящие ссылки сюда не попадают, так что искать
    // кандидатов можно по всему оставшемуся куску без доп. фильтрации.
    const gaps = findCandidateGaps(part, []);
    if (gaps.length === 0) { withGaps.push(part); return; }
    let rest = part;
    gaps.forEach((gap) => {
      const idx = rest.indexOf(gap);
      if (idx === -1) return;
      if (idx > 0) withGaps.push(rest.slice(0, idx));
      withGaps.push({ __gap: gap });
      rest = rest.slice(idx + gap.length);
    });
    if (rest) withGaps.push(rest);
  });

  return withGaps.map((part, i) => {
    if (typeof part === 'string') return <React.Fragment key={i}>{part}</React.Fragment>;
    if (part.__gap) {
      return (
        <GapMark
          key={i}
          data-fragment={part.__gap}
          $active={activeFragment === part.__gap}
          title="Похоже на библейскую ссылку, но не распознано — нажмите, чтобы отметить или сообщить"
          onClick={(e) => { e.stopPropagation(); if (onGapClick) onGapClick(part.__gap, e); }}
        >
          {part.__gap}
        </GapMark>
      );
    }
    return <LinkChip key={i} link={part.__link} active={activeFragment === part.__link.text} />;
  });
}

// «14-16, 18» -> [14, 15, 16, 18]. Дефис, en-dash и em-dash считаем одним и
// тем же: в исходниках уроков встречаются все три, а руками их не различить.
function expandNumberList(raw) {
  const out = [];
  String(raw || '').split(',').forEach((chunk) => {
    const part = chunk.trim();
    if (!part) return;
    const m = part.match(/^(\d+)\s*[-–—]\s*(\d+)$/);
    if (m) {
      const from = Number(m[1]);
      const to = Number(m[2]);
      if (from <= to) { for (let i = from; i <= to; i++) out.push(i); }
      else out.push(from);
      return;
    }
    const n = Number(part);
    if (Number.isFinite(n)) { out.push(n); return; }
    // Межглавный хвост вида «19–7:9»: в данных для такого хранится начальный
    // стих (см. подсказку в popover ссылки) — берём его, а не теряем всё поле.
    const lead = part.match(/^(\d+)/);
    if (lead) out.push(Number(lead[1]));
  });
  return out;
}

// Сокращения книг из BOOK_NAMES, длинные первыми: иначе «1 Ин» матчится как
// «Ин» и ссылка уезжает в другую книгу.
const BOOK_ABBR = Object.entries(BOOK_NAMES)
  .map(([num, [abbr]]) => ({ num: Number(num), abbr }))
  .sort((a, b) => b.abbr.length - a.abbr.length);

// Книги, у которых всего одна глава (список вербатим из Service.ts).
const ONE_CHAPTER_BOOKS = [640, 700, 710, 720];

// Разбор выделенного фрагмента в набор отрывков — чтобы форму не набивать
// руками. Специально проще боевой findsBibleLink: это лишь предзаполнение,
// человек всё равно смотрит глазами и правит.
//
// «Рим. 15:4, 16:22» -> [{520, гл. 15, ст. 4}, {520, гл. 16, ст. 22}]
// «Иер. 36:17, 18»   -> [{300, гл. 36, ст. 17, 18}] — часть без двоеточия
//                       это продолжение стихов, а не новая глава.
function parseFragmentToRefs(fragment) {
  const text = String(fragment || '').replace(/\s+/g, ' ').trim();
  const book = BOOK_ABBR.find(b => text.startsWith(b.abbr + '.') || text.startsWith(b.abbr + ' '));
  if (!book) return null;
  const tail = text.slice(book.abbr.length).replace(/^\.?\s*/, '');
  if (!tail) return null;

  // У книг в одну главу голое число — это стих, а не глава: «Иуд. 14, 15» —
  // стихи 14 и 15 первой главы. Список тот же, что в Service.ts.
  if (ONE_CHAPTER_BOOKS.includes(book.num) && !tail.includes(':')) {
    return [{ bookNumber: String(book.num), chapter: '1', verses: tail }];
  }

  const refs = [];
  // «;» и «и» разделяют отрывки одинаково: «Ис. 6:8; 53» и «Быт. 1 и 2».
  tail.split(/\s*;\s*|\s+и\s+/).forEach((piece) => {
    piece.split(',').forEach((rawPart, partIndex) => {
      const part = rawPart.trim();
      if (!part) return;
      const withVerses = part.match(/^(\d+)\s*:\s*(.+)$/);
      if (withVerses) {
        refs.push({ bookNumber: String(book.num), chapter: withVerses[1], verses: withVerses[2].trim() });
        return;
      }
      // Число без двоеточия — продолжение стихов, только если пришло ПОСЛЕ
      // запятой внутри того же куска: «Иер. 36:17, 18» — это стихи 17 и 18,
      // а «Ис. 6:8; 53» после точки с запятой — уже отдельная глава 53.
      const isContinuation = partIndex > 0 && refs.length && refs[refs.length - 1].verses;
      if (isContinuation && /^\d+(\s*[-–—]\s*\d+)?$/.test(part)) {
        refs[refs.length - 1].verses += `, ${part}`;
      } else if (/^\d+(\s*[-–—]\s*\d+)?$/.test(part)) {
        refs.push({ bookNumber: String(book.num), chapter: part, verses: '' });
      }
    });
  });
  return refs.length ? refs : null;
}

const EMPTY_REF = { bookNumber: '', chapter: '', verses: '' };

// «главы 15, 16 · стихи 4, 22» -> два отрывка: гл. 15 ст. 4 и гл. 16 ст. 22.
// Стихи раскидываются по главам попарно только когда их ровно столько же,
// сколько глав — в остальных случаях догадка была бы вредной: стихи остаются
// у первой главы, остальные главы уходят целиком, и человек видит это в
// строке «Сохранится как» до сохранения.
function splitRefByChapters(ref) {
  const chapters = expandNumberList(ref.chapter);
  const verses = expandNumberList(ref.verses);
  if (chapters.length < 2) return [ref];
  const pairwise = verses.length === chapters.length;
  return chapters.map((ch, i) => ({
    bookNumber: ref.bookNumber,
    chapter: String(ch),
    verses: pairwise ? String(verses[i]) : (i === 0 ? verses.join(', ') : ''),
  }));
}

function validateLessons(data) {
  if (!Array.isArray(data)) return 'Файл должен содержать массив уроков ([ ... ]).';
  if (data.length === 0) return 'Массив уроков пуст.';
  for (let i = 0; i < data.length; i++) {
    const l = data[i];
    if (!l || typeof l !== 'object') return `Урок #${i + 1}: не объект.`;
    if (!l.date) return `Урок #${i + 1}: отсутствует поле "date".`;
    if (!Array.isArray(l.content)) return `Урок #${i + 1} ("${l.name || '—'}"): отсутствует массив "content".`;
  }
  return null;
}

function fmtDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (isNaN(d)) return String(raw);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
}

// ─── Component ────────────────────────────────────────────────────────────────

function UploadTexts({ savedPassword }) {
  const [section, setSection] = useState('ss');
  const [file, setFile] = useState(null);
  const [lessons, setLessons] = useState(null);
  const [error, setError] = useState('');
  const [index, setIndex] = useState(0);
  const [uploading, setUploading] = useState(false);

  // PDF-разбор: файл дропнули, но год/дату субботы ещё нужно указать перед
  // отправкой на pdfparser (сам PDF дат для YSS не хранит, а год издания MR
  // не совпадает с годом чтений — см. pdf-importer/rules/*.md).
  const [pendingPdf, setPendingPdf] = useState(null);
  const [pdfYear, setPdfYear] = useState('');
  const [pdfFirstSaturday, setPdfFirstSaturday] = useState('');
  const [parsing, setParsing] = useState(false);

  // Режим «редактировать уже опубликованный урок» — вместо дропзоны,
  // выбор даты и подгрузка через существующий GET /get-lesson/:type.
  const [editMode, setEditMode] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [loadingExisting, setLoadingExisting] = useState(false);
  // Список дат всех уроков раздела/года (через уже существующий
  // GET /get-lessons-metadata/:type) — нужен только чтобы стрелками
  // "Назад"/"Вперёд" листать СОСЕДНИЕ уже опубликованные уроки, не
  // возвращаясь каждый раз к форме поиска по дате. null = не в этом режиме
  // (обычный предпросмотр только что распознанного/загруженного файла).
  const [existingDates, setExistingDates] = useState(null);
  const [existingIndex, setExistingIndex] = useState(0);
  const [navigatingExisting, setNavigatingExisting] = useState(false);

  // Правки: выделили фрагмент -> отметили как ссылку (авто или вручную).
  // Копятся локально, применяются разом через /apply-corrections.
  const [corrections, setCorrections] = useState([]);
  const [reapplying, setReapplying] = useState(false);
  const [selection, setSelection] = useState(null); // {text, contentId, lessonIndex, top, left}
  const [corrForm, setCorrForm] = useState(null);

  // «Сообщить о проблеме» — копирует контекст в буфер обмена (нет своего
  // тикет-трекера, поэтому просто готовый текст для вставки в чат/письмо).
  const [reportTarget, setReportTarget] = useState(null);

  // Автопроверки распознанного материала: панель проблем справа от листа.
  const [sevFilter, setSevFilter] = useState(null);   // null = показывать все
  const [activeIssue, setActiveIssue] = useState(null);
  const [scrollTarget, setScrollTarget] = useState(null);
  const [forceUpload, setForceUpload] = useState(false);
  // Список проблем по умолчанию сквозной — по всем урокам сразу: при 91 уроке
  // открывать каждый и высматривать подсветку вручную нереально.
  const [onlyThisLesson, setOnlyThisLesson] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [onlyProblemLessons, setOnlyProblemLessons] = useState(false);
  // Меню действий над жёлтым «похоже на ссылку» фрагментом.
  const [gapMenu, setGapMenu] = useState(null);
  const issueRowRefs = useRef({});
  const sheetRef = useRef(null);
  const blockRefs = useRef({});

  const reset = () => {
    setFile(null); setLessons(null); setError(''); setIndex(0);
    setPendingPdf(null); setPdfYear(''); setPdfFirstSaturday('');
    setCorrections([]); setEditMode(false); setEditDate('');
    setExistingDates(null); setExistingIndex(0);
    setActiveIssue(null); setSevFilter(null); setForceUpload(false);
  };

  const onDrop = useCallback((accepted) => {
    const f = accepted[0];
    if (!f) return;
    setError('');

    const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
    if (isPdf) {
      const detected = detectSectionFromName(f.name);
      if (detected) setSection(detected);
      setPendingPdf(f);
      setFile(null); setLessons(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      let parsed;
      try {
        parsed = JSON.parse(e.target.result);
      } catch {
        setError('Не удалось разобрать JSON — файл повреждён или это не JSON.');
        setFile(null); setLessons(null);
        return;
      }
      const err = validateLessons(parsed);
      if (err) {
        setError(err);
        setFile(null); setLessons(null);
        return;
      }
      // Автоопределение раздела по имени файла
      const detected = detectSectionFromName(f.name);
      if (detected) setSection(detected);
      setFile(f);
      setLessons(parsed);
      setIndex(0);
    };
    reader.readAsText(f);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/json': ['.json'], 'application/pdf': ['.pdf'] },
    multiple: false,
  });

  const currentSection = SECTIONS.find(s => s.key === section);

  const parsePdf = async () => {
    if (!pendingPdf) return;
    if (!currentSection.pdfParseType) {
      toast.error('Для этого раздела PDF-разбор пока не откалиброван.');
      return;
    }
    const isYss = currentSection.pdfParseType === 'yss';
    if (isYss && !pdfFirstSaturday) { toast.error('Укажите дату субботы 1-й недели.'); return; }
    if (!isYss && !pdfYear) { toast.error('Укажите год.'); return; }

    setParsing(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', pendingPdf);
      if (isYss) formData.append('firstSaturday', pdfFirstSaturday);
      else formData.append('year', pdfYear);

      const res = await fetch(`${API_URL}/api/text/parse-pdf/${currentSection.pdfParseType}`, {
        method: 'POST', body: formData,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 413 от nginx тут почти всегда врёт: лимит на тело — 1000 МБ, а такой
        // ответ приходит, когда прокси оборвал ещё не дочитанный запрос по
        // таймауту. 504 — то же самое, только честно названное.
        const timedOut = res.status === 413 || res.status === 504;
        setError(
          body.error
            ? `${body.error}${body.stderr ? '\n' + body.stderr.slice(-500) : ''}`
            : timedOut
              ? `Сервер не дождался разбора (HTTP ${res.status}). PDF за целый год разбирается около минуты — попробуйте ещё раз, а если повторяется, надо поднимать таймаут прокси.`
              : `Ошибка разбора (HTTP ${res.status})`,
        );
        return;
      }
      const err = validateLessons(body.lessons);
      if (err) { setError(err); return; }

      setFile(pendingPdf);
      setLessons(body.lessons);
      setIndex(0);
      setPendingPdf(null);
      toast.success(`Распознано: ${body.lessons.length} уроков`);
    } catch {
      setError('Нет соединения с сервером');
    } finally {
      setParsing(false);
    }
  };

  const loadExistingLesson = async () => {
    if (!editDate) { toast.error('Укажите дату урока.'); return; }
    setLoadingExisting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/text/get-lesson/${section}?date=${editDate}`);
      const data = await res.json().catch(() => null);
      if (!res.ok || !data) { setError(`Урок не найден (HTTP ${res.status})`); return; }
      // Запись из БД хранит контент в поле `text` (см. text.service.ts
      // findLessonByExactDate) -- приводим к тому же `content`, что и везде
      // в превью/при повторной отправке.
      const single = { ...data, content: data.text };
      setFile({ name: `${section}-${editDate}.json` });
      setLessons([single]);
      setIndex(0);
      setCorrections([]);
      setEditMode(false);

      // Список дат всех уроков раздела за этот год — чтобы стрелками можно
      // было листать к соседним уже опубликованным урокам (см. state выше).
      // Не блокирующая ошибка: если метаданные не подтянулись, просто не
      // будет соседней навигации -- сам загруженный урок всё равно на месте.
      try {
        const year = editDate.slice(0, 4);
        const metaRes = await fetch(`${API_URL}/api/text/get-lessons-metadata/${section}?year=${year}`);
        const metaData = await metaRes.json().catch(() => null);
        if (metaRes.ok && Array.isArray(metaData?.items)) {
          const items = [...metaData.items].sort((a, b) => a.date.localeCompare(b.date));
          const pos = items.findIndex(it => it.date === editDate);
          setExistingDates(items);
          setExistingIndex(pos >= 0 ? pos : 0);
        } else {
          setExistingDates(null);
        }
      } catch {
        setExistingDates(null);
      }
    } catch {
      setError('Нет соединения с сервером');
    } finally {
      setLoadingExisting(false);
    }
  };

  // Загрузить урок раздела по абсолютному индексу в existingDates (соседний
  // через ±1 со стрелок, либо произвольный через выпадающий список).
  const gotoExistingIndex = async (newIndex) => {
    if (!existingDates || newIndex < 0 || newIndex >= existingDates.length) return;
    const target = existingDates[newIndex];
    setNavigatingExisting(true);
    try {
      const res = await fetch(`${API_URL}/api/text/get-lesson/${section}?date=${target.date}`);
      const data = await res.json().catch(() => null);
      if (!res.ok || !data) { toast.error(`Не удалось загрузить урок (HTTP ${res.status})`); return; }
      const single = { ...data, content: data.text };
      setLessons([single]);
      setFile({ name: `${section}-${target.date}.json` });
      setExistingIndex(newIndex);
      setCorrections([]);
    } catch {
      toast.error('Нет соединения с сервером');
    } finally {
      setNavigatingExisting(false);
    }
  };

  // Статистика по типам блоков
  const stats = useMemo(() => {
    if (!lessons) return null;
    const dates = lessons.map(l => new Date(l.date)).filter(d => !isNaN(d)).sort((a, b) => a - b);
    const typeCounts = {};
    lessons.forEach(l => (l.content || []).forEach(b => {
      typeCounts[b.type] = (typeCounts[b.type] || 0) + 1;
    }));
    // Блоки неизвестного типа теперь считает и показывает панель автопроверок.
    return {
      count: lessons.length,
      from: dates[0],
      to: dates[dates.length - 1],
      typeCounts,
    };
  }, [lessons]);

  // ── Автопроверки распознанного материала ──
  const issues = useMemo(() => {
    if (!lessons) return [];
    return runChecks(lessons, {
      section,
      knownTypes: Object.keys(BLOCK_KIND),
      knownBooks: Object.keys(BOOK_NAMES).map(Number),
      kindOf: (t) => BLOCK_KIND[t] || 'unknown',
    });
  }, [lessons, section]);

  const issueCounts = useMemo(() => countBySeverity(issues), [issues]);
  const issueBarTone = BAR_TONE[
    issueCounts.error ? 'error' : issueCounts.warn ? 'warn' : issueCounts.info ? 'info' : 'ok'
  ];
  const issuesByLesson = useMemo(() => groupByLesson(issues), [issues]);
  const visibleIssues = useMemo(
    () => issues.filter(i => (
      (!sevFilter || i.severity === sevFilter)
      && (!onlyThisLesson || i.lessonIndex === index)
    )),
    [issues, sevFilter, onlyThisLesson, index],
  );

  // Проблемы текущего урока — для строки состояния.
  const currentLessonCounts = issuesByLesson.get(index) || { error: 0, warn: 0, info: 0, total: 0 };

  // Индексы уроков, где есть хоть одна проблема (с учётом фильтра по severity) —
  // по ним ходят кнопки «предыдущий/следующий проблемный урок» и строится
  // сокращённый список в селекторе.
  const problemLessonIndexes = useMemo(() => {
    const set = new Set();
    issues.forEach(i => { if (!sevFilter || i.severity === sevFilter) set.add(i.lessonIndex); });
    return [...set].sort((a, b) => a - b);
  }, [issues, sevFilter]);

  // Позиция активной проблемы в текущем списке — «3 / 47» в строке состояния.
  const activePos = useMemo(
    () => (activeIssue ? visibleIssues.findIndex(i => i.key === activeIssue.key) : -1),
    [visibleIssues, activeIssue],
  );

  // Переход к проблеме: переключаем урок, а прокрутку делаем после
  // перерисовки — до неё нужного элемента в DOM ещё нет.
  const goToIssue = useCallback((issue) => {
    if (!issue) return;
    setActiveIssue(issue);
    setGapMenu(null);
    setIndex(issue.lessonIndex);
    setScrollTarget({ blockId: issue.blockId, fragment: issue.fragment || null });
  }, []);

  useEffect(() => {
    if (!scrollTarget) return undefined;
    const raf = requestAnimationFrame(() => {
      const sheet = sheetRef.current;
      if (sheet) {
        const block = scrollTarget.blockId != null ? blockRefs.current[scrollTarget.blockId] : null;
        // Внутри блока целимся в сам проблемный фрагмент, если он известен:
        // блок бывает на пол-экрана, и «прокрутка к блоку» ещё не значит, что
        // глазами нашёл нужные три слова.
        let node = block;
        if (block && scrollTarget.fragment) {
          const marks = block.querySelectorAll('[data-fragment]');
          for (let i = 0; i < marks.length; i++) {
            if (marks[i].getAttribute('data-fragment') === scrollTarget.fragment) { node = marks[i]; break; }
          }
        }
        if (node) {
          // offsetTop здесь не годится: у блока свой position: relative, и для
          // вложенного span отсчёт пошёл бы от блока, а не от листа.
          const sheetRect = sheet.getBoundingClientRect();
          const nodeRect = node.getBoundingClientRect();
          const delta = (nodeRect.top - sheetRect.top) - sheet.clientHeight / 2 + nodeRect.height / 2;
          sheet.scrollTop = Math.max(0, sheet.scrollTop + delta);
        } else {
          sheet.scrollTop = 0;
        }
      }
      setScrollTarget(null);
    });
    return () => cancelAnimationFrame(raf);
  }, [scrollTarget, index]);

  // Строка активной проблемы в панели тоже должна быть видна — иначе после
  // прыжка по хоткею непонятно, где ты в списке.
  useEffect(() => {
    if (!activeIssue) return;
    const row = issueRowRefs.current[activeIssue.key];
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [activeIssue]);

  const stepIssue = useCallback((delta) => {
    if (!visibleIssues.length) return;
    const at = visibleIssues.findIndex(i => activeIssue && i.key === activeIssue.key);
    const next = at < 0
      ? (delta > 0 ? 0 : visibleIssues.length - 1)
      : (at + delta + visibleIssues.length) % visibleIssues.length;
    goToIssue(visibleIssues[next]);
  }, [visibleIssues, activeIssue, goToIssue]);

  // Ходить по проблемам, не снимая рук с клавиатуры. Alt, а не голые стрелки —
  // чтобы не мешать прокрутке листа и вводу в формах правок.
  useEffect(() => {
    if (!lessons) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { setActiveIssue(null); setGapMenu(null); return; }
      if (!e.altKey) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); stepIssue(1); }
      if (e.key === 'ArrowUp') { e.preventDefault(); stepIssue(-1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lessons, stepIssue]);

  // Следующий/предыдущий урок, в котором вообще есть что смотреть.
  const stepProblemLesson = (delta) => {
    if (!problemLessonIndexes.length) return;
    const after = problemLessonIndexes.filter(i => (delta > 0 ? i > index : i < index));
    const target = delta > 0
      ? (after[0] ?? problemLessonIndexes[0])
      : (after[after.length - 1] ?? problemLessonIndexes[problemLessonIndexes.length - 1]);
    const first = visibleIssues.find(i => i.lessonIndex === target);
    if (first) goToIssue(first);
    else { setIndex(target); setActiveIssue(null); }
  };

  // Клик по жёлтому фрагменту: подсвечиваем его же строку в панели (если
  // проверка её завела) и показываем меню действий рядом с фрагментом.
  const handleGapClick = useCallback((fragment, block, blockId, event) => {
    const norm = String(fragment).replace(/\s+/g, ' ').trim();
    const issue = issues.find(i => (
      i.lessonIndex === index
      && i.code === 'link-missed'
      && String(i.fragment || i.message).replace(/\s+/g, ' ').includes(norm)
    ));
    if (issue) setActiveIssue(issue);
    const rect = event.currentTarget.getBoundingClientRect();
    setSelection(null);
    setGapMenu({
      fragment,
      contentId: blockId,
      type: block.type,
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  }, [issues, index]);

  // «Отметить как ссылку» прямо из жёлтого фрагмента — без повторного
  // выделения мышью того, что подсветка уже нашла.
  const markGapAsLink = () => {
    if (!gapMenu) return;
    setCorrForm({
      text: gapMenu.fragment,
      contentId: gapMenu.contentId,
      lessonIndex: index,
      mode: 'auto',
      refs: parseFragmentToRefs(gapMenu.fragment) || [{ ...EMPTY_REF }],
    });
    setGapMenu(null);
  };

  const reportGap = () => {
    if (!gapMenu) return;
    setReportTarget({
      lessonIndex: index,
      contentId: gapMenu.contentId,
      type: gapMenu.type,
      text: gapMenu.fragment,
      note: '',
    });
    setGapMenu(null);
  };

  const copyIssuesReport = async () => {
    try {
      await navigator.clipboard.writeText(issuesToText(issues, lessons));
      toast.success('Список проблем скопирован');
    } catch {
      toast.error('Браузер не дал доступ к буферу обмена');
    }
  };

  // Какие виды блоков реально встречаются — для легенды
  const usedKinds = useMemo(() => {
    if (!lessons) return [];
    const set = new Set();
    lessons.forEach(l => (l.content || []).forEach(b => set.add(BLOCK_KIND[b.type] || 'unknown')));
    return Object.keys(KIND_META).filter(k => set.has(k));
  }, [lessons]);

  // Правки, ожидающие применения именно к сейчас открытому уроку — для
  // бейджей на блоках.
  const correctionsCountByContentId = useMemo(() => {
    const map = {};
    corrections.forEach(c => {
      if (c.lessonIndex !== index) return;
      map[c.contentId] = (map[c.contentId] || 0) + 1;
    });
    return map;
  }, [corrections, index]);

  const handleTextSelection = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) { setSelection(null); return; }
    const text = sel.toString().trim();
    if (!text) { setSelection(null); return; }
    let node = sel.anchorNode;
    while (node && node.nodeType !== 1) node = node.parentNode;
    const blockEl = node?.closest?.('[data-content-id]');
    if (!blockEl) { setSelection(null); return; }
    const rawId = blockEl.getAttribute('data-content-id');
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    setSelection({
      text,
      contentId: isNaN(Number(rawId)) ? rawId : Number(rawId),
      lessonIndex: index,
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  }, [index]);

  const openCorrectionForm = () => {
    if (!selection) return;
    setCorrForm({ ...selection, mode: 'auto', refs: parseFragmentToRefs(selection.text) || [{ ...EMPTY_REF }] });
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const saveCorrection = () => {
    if (!corrForm) return;
    const correction = {
      lessonIndex: corrForm.lessonIndex,
      contentId: corrForm.contentId,
      fragment: corrForm.text,
      mode: corrForm.mode,
    };
    if (corrForm.mode === 'manual') {
      const rows = (corrForm.refs || []).filter(r => r.bookNumber || r.chapter || r.verses);
      if (!rows.length) { toast.error('Заполните хотя бы один отрывок.'); return; }
      if (rows.some(r => !r.bookNumber)) { toast.error('В каждом отрывке выберите книгу.'); return; }
      if (rows.some(r => !expandNumberList(r.chapter).length)) { toast.error('В каждом отрывке укажите главу.'); return; }
      // Приложение при заполненных стихах читает только chapter[0]
      // (useModalVerse.ts: SQLiteService.getVerses(..., link.chapter[0], ...)),
      // так что «главы 15, 16» + «стихи 4, 22» молча показали бы 15-ю главу.
      // Такие случаи — это всегда РАЗНЫЕ отрывки, их и просим развести.
      if (rows.some(r => expandNumberList(r.chapter).length > 1 && expandNumberList(r.verses).length)) {
        toast.error('В одном отрывке нельзя указать несколько глав вместе со стихами — заведите отдельный отрывок на каждую главу.');
        return;
      }
      // Одна ссылка = один подсвеченный фрагмент текста, но внутри может быть
      // несколько отрывков: «Рим. 15:4, 16:22» — это Рим. 15:4 и Рим. 16:22,
      // двумя записями в data (формат это поддерживает, см. «Ис. 2:4; 5:20»).
      const refs = rows.map(r => ({
        bookNumber: Number(r.bookNumber),
        chapter: expandNumberList(r.chapter),
        verses: expandNumberList(r.verses).length ? expandNumberList(r.verses) : null,
      }));
      correction.manual = {
        refs,
        // Первый отрывок дублируем плоскими полями — на случай, если
        // pdfparser на той стороне ещё не знает про refs.
        bookNumber: refs[0].bookNumber,
        chapter: refs[0].chapter,
        verses: refs[0].verses,
      };
    }
    setCorrections(cs => [...cs, correction]);
    setCorrForm(null);
    toast.info('Правка добавлена — нажмите «Повторно распознать», чтобы применить.');
  };

  const reapplyCorrections = async () => {
    if (!corrections.length || !lessons) return;
    setReapplying(true);
    try {
      const res = await fetch(`${API_URL}/api/text/apply-corrections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lessons, corrections }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data) {
        toast.error(data?.error || `Ошибка применения правок (HTTP ${res.status})`);
        return;
      }
      setLessons(data.lessons);
      setCorrections([]);
      if (data.skipped?.length) {
        toast.warn(`Применено: ${data.applied}. Пропущено: ${data.skipped.length} — см. консоль браузера.`);
        console.warn('pdfparser: пропущенные правки', data.skipped);
      } else {
        toast.success(`Применено правок: ${data.applied}`);
      }
    } catch {
      toast.error('Нет соединения с сервером');
    } finally {
      setReapplying(false);
    }
  };

  const copyReport = () => {
    if (!reportTarget) return;
    const lessonForReport = lessons[reportTarget.lessonIndex];
    const lines = [
      `Раздел: ${currentSection?.label}`,
      `Файл: ${file?.name || '—'}`,
      `Урок: ${fmtDate(lessonForReport?.date)} — ${lessonForReport?.name || 'без названия'}`,
      `Блок: [${reportTarget.type}] id=${reportTarget.contentId}`,
      `Текст: ${reportTarget.text}`,
      reportTarget.note ? `Комментарий: ${reportTarget.note}` : null,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(lines).then(
      () => toast.success('Отчёт скопирован в буфер обмена'),
      () => toast.error('Не удалось скопировать'),
    );
  };

  // Имя файла для сохранения/отправки — из исходного (PDF или JSON), но
  // всегда с расширением .json, т.к. содержимое всегда пересобирается заново
  // из текущего состояния `lessons` (см. комментарий в handleSubmit ниже).
  const jsonFileName = () => (file?.name || 'lessons').replace(/\.(pdf|json)$/i, '') + '.json';

  // Скачать текущий результат (с уже применёнными правками, если были) как
  // файл на диск — до отправки на сервер. Полезно как чекпойнт: если нужно
  // будет ещё раз доработать урок, но заново парсить PDF (а значит и терять
  // уже сделанные правки) не хочется — можно перетащить обратно этот же
  // .json и продолжить с того места, где остановились.
  const downloadJson = () => {
    if (!lessons) return;
    const blob = new Blob([JSON.stringify(lessons, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = jsonFileName();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('JSON сохранён на диск');
  };

  const handleSubmit = async () => {
    if (!lessons) return;
    const sec = currentSection;
    setUploading(true);
    try {
      // Всегда собираем JSON заново из текущего состояния `lessons` — а не
      // пересылаем исходный дропнутый файл: для PDF-разбора это был бы сам
      // PDF (неправильно), а после применённых правок исходный файл в любом
      // случае устарел.
      const jsonBlob = new Blob([JSON.stringify(lessons)], { type: 'application/json' });
      const jsonName = jsonFileName();
      const jsonFile = new File([jsonBlob], jsonName, { type: 'application/json' });

      const formData = new FormData();
      formData.append('file', jsonFile);
      if (savedPassword) formData.append('password', savedPassword);
      const res = await fetch(`${API_URL}${sec.endpoint}`, { method: 'POST', body: formData });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.message && !/error/i.test(body.message)) {
        toast.success(`«${sec.label}» загружена: ${lessons.length} уроков`);
        reset();
      } else {
        toast.error(body.message || `Ошибка загрузки (HTTP ${res.status})`);
      }
    } catch {
      toast.error('Нет соединения с сервером');
    } finally {
      setUploading(false);
    }
  };

  const lesson = lessons ? lessons[index] : null;

  return (
    <Card>
      <Tabs>
        {SECTIONS.map(s => (
          <Tab
            key={s.key}
            active={section === s.key}
            onClick={() => setSection(s.key)}
          >
            {s.label}
          </Tab>
        ))}
      </Tabs>

      <FormatGuide>
        <GuideTitle>Формат файла</GuideTitle>
        <GuideNote>JSON-массив уроков (серверный формат «+»): каждый урок содержит <code>date</code>, <code>name</code> и массив <code>content</code> с блоками.</GuideNote>
        <GuideNote>PDF-буклет распознаётся автоматически (шрифты + библейские ссылки) — предпросмотр ниже такой же, как у готового JSON.</GuideNote>
        <GuideNote>Раздел определяется автоматически по имени файла (SS / YSS / MR / ER), но его можно сменить вкладкой выше.</GuideNote>
      </FormatGuide>

      {!lessons && !pendingPdf && (
        <ModeSwitchRow>
          <LinkBtn onClick={() => { setEditMode(m => !m); setError(''); }}>
            {editMode ? '← Вернуться к загрузке нового файла' : 'Редактировать уже опубликованный урок →'}
          </LinkBtn>
        </ModeSwitchRow>
      )}

      {!lessons && !pendingPdf && editMode && (
        <PdfParamsBox>
          <GuideTitle>Найти урок для правки</GuideTitle>
          <InlineForm>
            <FieldRow style={{ marginBottom: 0, minWidth: 200 }}>
              <FieldLabel>Дата урока</FieldLabel>
              <TextInput type="date" value={editDate} onChange={e => setEditDate(e.target.value)} />
            </FieldRow>
            <PrimaryBtn style={{ flex: 'none', padding: '0.5rem 1.1rem' }} disabled={loadingExisting} onClick={loadExistingLesson}>
              {loadingExisting ? 'Загрузка…' : 'Загрузить'}
            </PrimaryBtn>
          </InlineForm>
          <GuideNote style={{ marginTop: '0.6rem' }}>
            Раздел — {currentSection?.label} (переключается вкладкой выше). Для YSS дата — суббота нужной недели.
          </GuideNote>
        </PdfParamsBox>
      )}

      {!lessons && !pendingPdf && !editMode && (
        <DropArea {...getRootProps()} isDragActive={isDragActive}>
          <input {...getInputProps()} />
          <DropIcon>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
          </DropIcon>
          <DropText>
            {isDragActive ? 'Отпустите файл…' : <><strong>Выберите JSON или PDF</strong> или перетащите сюда</>}
          </DropText>
          <DropHint>
            .json (готовый результат) или .pdf (будет распознан автоматически) · Один файл за раз
            {!currentSection?.pdfParseType && ' · PDF для этого раздела пока не откалиброван'}
          </DropHint>
        </DropArea>
      )}

      {pendingPdf && (
        <PdfParamsBox>
          <GuideTitle>Параметры разбора PDF</GuideTitle>
          <GuideNote style={{ marginBottom: '0.85rem' }}>
            Файл: <strong>{pendingPdf.name}</strong> · раздел — {currentSection?.label}
          </GuideNote>
          {currentSection?.pdfParseType === 'yss' ? (
            <FieldRow>
              <FieldLabel>Суббота 1-й недели квартала</FieldLabel>
              <TextInput type="date" value={pdfFirstSaturday} onChange={e => setPdfFirstSaturday(e.target.value)} />
              <GuideNote style={{ marginTop: '0.4rem' }}>YSS дат в PDF не печатает — сверьте с уже загруженной «Субботней школой» этого же квартала.</GuideNote>
            </FieldRow>
          ) : (
            <FieldRow>
              <FieldLabel>Год</FieldLabel>
              <TextInput type="number" placeholder="напр. 2026" value={pdfYear} onChange={e => setPdfYear(e.target.value)} />
              {currentSection?.key === 'mr' && (
                <GuideNote style={{ marginTop: '0.4rem' }}>Обложка печатает год издания, а не год чтений — сверьте по дню недели у 1 января.</GuideNote>
              )}
            </FieldRow>
          )}
          <CorrActions>
            <GhostBtn onClick={() => { setPendingPdf(null); setPdfYear(''); setPdfFirstSaturday(''); }}>Отмена</GhostBtn>
            <PrimaryBtn disabled={parsing} onClick={parsePdf}>{parsing ? 'Распознаём…' : 'Распознать'}</PrimaryBtn>
          </CorrActions>
          {parsing && (
            <GuideNote style={{ margin: '0.6rem 0 0' }}>
              Буклет за целый год разбирается около минуты — это нормально, не закрывайте вкладку.
            </GuideNote>
          )}
        </PdfParamsBox>
      )}

      {error && <ErrorMsg>⚠ {error}</ErrorMsg>}

      {lessons && stats && (
        <>
          <FileBar>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
            <span>{file?.name}</span>
            <NavBtn onClick={downloadJson} title="Сохранить текущий результат (с учётом уже применённых правок) на диск">
              ⭳ Скачать JSON
            </NavBtn>
            <NavBtn onClick={reset}>Заменить файл</NavBtn>
          </FileBar>

          <Summary>
            <Stat><span>Раздел</span><strong>{SECTIONS.find(s => s.key === section)?.label}</strong></Stat>
            <Stat><span>Уроков</span><strong>{stats.count}</strong></Stat>
            <Stat><span>С даты</span><strong>{fmtDate(stats.from)}</strong></Stat>
            <Stat><span>По дату</span><strong>{fmtDate(stats.to)}</strong></Stat>
          </Summary>

          <Legend>
            {usedKinds.map(k => (
              <LegendDot key={k} color={KIND_META[k].color}>{KIND_META[k].label}</LegendDot>
            ))}
            <LegendDot color={LINK_COLOR}>Библейская ссылка</LegendDot>
            <LegendDot color="#ca8a04">Похоже на ссылку, не распознано</LegendDot>
          </Legend>

          <GuideNote style={{ margin: '0 0 0.75rem' }}>
            Выделите фрагмент текста ниже, чтобы отметить его как библейскую ссылку вручную,
            либо нажмите на <GapMark as="span" style={{ cursor: 'default' }}>жёлтый фрагмент</GapMark> — это то,
            что похоже на ссылку, но не распозналось.
          </GuideNote>

          {/* Строка состояния автопроверок. Липкая и всегда на виду: панель
              справа может быть свёрнута или уехать под лист на узком экране,
              а ответ на вопрос «есть ли тут ошибки» нужен постоянно. */}
          <IssueBar $tone={issueBarTone}>
            <BarTitle $tone={issueBarTone}>
              {issues.length === 0
                ? '✓ Автопроверка не нашла проблем'
                : `${issueCounts.error ? `🔴 ${issueCounts.error}` : ''} ${issueCounts.warn ? `🟡 ${issueCounts.warn}` : ''} ${issueCounts.info ? `🔵 ${issueCounts.info}` : ''}`.trim()}
            </BarTitle>
            {issues.length > 0 && (
              <BarNote>
                во всём файле · в этом уроке: {currentLessonCounts.total || 0}
                {problemLessonIndexes.length ? ` · уроков с проблемами: ${problemLessonIndexes.length} из ${lessons.length}` : ''}
              </BarNote>
            )}
            <BarSpacer />
            {issues.length > 0 && (
              <>
                <BarNote>
                  {activePos >= 0 ? `${activePos + 1} / ${visibleIssues.length}` : `${visibleIssues.length} в списке`}
                </BarNote>
                <BarBtn type="button" disabled={!visibleIssues.length} onClick={() => stepIssue(-1)} title="Alt + ↑">↑ Пред.</BarBtn>
                <BarBtn type="button" disabled={!visibleIssues.length} onClick={() => stepIssue(1)} title="Alt + ↓">↓ След.</BarBtn>
                <BarBtn type="button" onClick={() => setPanelOpen(o => !o)}>
                  {panelOpen ? 'Скрыть список' : 'Показать список'}
                </BarBtn>
              </>
            )}
          </IssueBar>

          {/* Два разных источника навигации: обычный предпросмотр листает
              ВНУТРИ уже загрученного массива `lessons` (index); режим
              «редактировать опубликованный урок» листает ПО СПИСКУ ДАТ
              раздела (existingDates/existingIndex), подгружая с сервера
              каждый следующий/предыдущий урок отдельным запросом — массив
              `lessons` в этом режиме всегда содержит ровно один элемент. */}
          <NavBar>
            {existingDates ? (
              <>
                <NavBtn disabled={navigatingExisting || existingIndex === 0} onClick={() => gotoExistingIndex(existingIndex - 1)}>← Назад</NavBtn>
                <LessonSelect
                  value={existingIndex}
                  disabled={navigatingExisting}
                  onChange={e => gotoExistingIndex(Number(e.target.value))}
                >
                  {existingDates.map((it, i) => (
                    <option key={it.id ?? i} value={i}>
                      {i + 1}. {fmtDate(it.date)} — {it.name || 'Без названия'}
                    </option>
                  ))}
                </LessonSelect>
                <NavBtn disabled={navigatingExisting || existingIndex === existingDates.length - 1} onClick={() => gotoExistingIndex(existingIndex + 1)}>
                  {navigatingExisting ? 'Загрузка…' : 'Вперёд →'}
                </NavBtn>
              </>
            ) : (
              <>
                <NavBtn disabled={index === 0} onClick={() => setIndex(i => Math.max(0, i - 1))}>← Назад</NavBtn>
                <LessonSelect
                  value={index}
                  onChange={e => { setIndex(Number(e.target.value)); setActiveIssue(null); }}
                >
                  {lessons.map((l, i) => {
                    const c = issuesByLesson.get(i);
                    // Режим «только с проблемами» не выкидывает пункты из
                    // списка (иначе value перестанет совпадать с индексом
                    // урока), а прячет их: выпадающий список схлопывается до
                    // тех уроков, ради которых его и открывают.
                    if (onlyProblemLessons && !c && i !== index) return null;
                    const badge = c ? ` ${c.error ? '🔴' : c.warn ? '🟡' : '🔵'}${c.total}` : '';
                    return (
                      <option key={i} value={i}>
                        {i + 1}. {fmtDate(l.date)} — {l.name || 'Без названия'}{badge}
                      </option>
                    );
                  })}
                </LessonSelect>
                <NavBtn disabled={index === lessons.length - 1} onClick={() => setIndex(i => Math.min(lessons.length - 1, i + 1))}>Вперёд →</NavBtn>
                {problemLessonIndexes.length > 0 && (
                  <>
                    <NavBtn
                      type="button"
                      onClick={() => stepProblemLesson(-1)}
                      title="Предыдущий урок, в котором автопроверка что-то нашла"
                    >⚠ ←</NavBtn>
                    <NavBtn
                      type="button"
                      onClick={() => stepProblemLesson(1)}
                      title="Следующий урок, в котором автопроверка что-то нашла"
                    >⚠ →</NavBtn>
                  </>
                )}
              </>
            )}
          </NavBar>
          {!existingDates && problemLessonIndexes.length > 0 && (
            <GuideNote style={{ margin: '-0.5rem 0 0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <input
                type="checkbox"
                checked={onlyProblemLessons}
                onChange={e => setOnlyProblemLessons(e.target.checked)}
              />
              Показывать в списке только уроки с проблемами ({problemLessonIndexes.length} из {lessons.length})
            </GuideNote>
          )}
          {existingDates && (
            <GuideNote style={{ margin: '-0.5rem 0 0.75rem' }}>
              Урок {existingIndex + 1} из {existingDates.length} опубликованных в разделе «{currentSection?.label}» за {editDate.slice(0, 4)} год.
            </GuideNote>
          )}

          <PreviewLayout $withPanel={panelOpen}>
          <PreviewSheet onMouseUp={handleTextSelection} ref={sheetRef}>
            <LessonHead>
              <h3>
                {lesson.lessonNumber ? `Урок ${lesson.lessonNumber}. ` : ''}{lesson.name || 'Без названия'}
              </h3>
              <p>{fmtDate(lesson.date)}</p>
            </LessonHead>

            {(lesson.content || []).map((block, i) => {
              const kind = BLOCK_KIND[block.type] || 'unknown';
              const pendingCount = correctionsCountByContentId[block.id ?? i];
              const blockId = block.id ?? i;
              const isActive = !!activeIssue && activeIssue.lessonIndex === index && activeIssue.blockId === blockId;
              return (
                <Block
                  key={blockId}
                  kind={kind}
                  data-content-id={blockId}
                  style={{ position: 'relative' }}
                  $active={isActive}
                  ref={(el) => { blockRefs.current[blockId] = el; }}
                >
                  {pendingCount > 0 && <CorrectionBadge>✏️ {pendingCount}</CorrectionBadge>}
                  <TypeTag color={KIND_META[kind].color}>{block.type}</TypeTag>
                  {kind === 'list' && <span>• </span>}
                  {renderTextWithLinks(block.text, block.links, {
                    activeFragment: isActive ? (activeIssue.fragment || null) : null,
                    onGapClick: (fragment, e) => handleGapClick(fragment, block, blockId, e),
                  })}
                  {kind === 'question' && <QHint>↳ поле для ответа пользователя в приложении</QHint>}
                  {' '}
                  <ReportBtn
                    type="button"
                    title="Сообщить о проблеме с этим блоком"
                    onClick={() => setReportTarget({ lessonIndex: index, contentId: block.id ?? i, type: block.type, text: block.text, note: '' })}
                  >
                    ⚠ сообщить
                  </ReportBtn>
                </Block>
              );
            })}
          </PreviewSheet>

            {panelOpen && (
            <Panel>
              <PanelHead>
                <PanelTitle>Автопроверка · {issues.length} {issues.length === 1 ? 'замечание' : 'замечаний'}</PanelTitle>
                <ChipRow>
                  {SEVERITY_ORDER.filter(s => issueCounts[s] > 0).map(s => (
                    <Chip
                      key={s}
                      type="button"
                      $color={SEVERITY_META[s].color}
                      $on={sevFilter === s}
                      onClick={() => setSevFilter(sevFilter === s ? null : s)}
                    >
                      {SEVERITY_META[s].label}: {issueCounts[s]}
                    </Chip>
                  ))}
                  <Chip
                    type="button"
                    $color="#4f46e5"
                    $on={onlyThisLesson}
                    onClick={() => setOnlyThisLesson(v => !v)}
                    title="По умолчанию список сквозной — по всем урокам файла"
                  >
                    Только этот урок
                  </Chip>
                </ChipRow>
              </PanelHead>

              <IssueList>
                {visibleIssues.length === 0 && (
                  <PanelOk>
                    {issues.length === 0 ? '✓ Проверки не нашли проблем' : 'По этому фильтру ничего нет'}
                  </PanelOk>
                )}
                {visibleIssues.map((issue, n) => {
                  // Заголовок группы печатаем на первой строке каждого урока:
                  // список сквозной, и без разделителей 300 строк подряд
                  // читаются как одна каша.
                  const prev = n > 0 ? visibleIssues[n - 1] : null;
                  const newLesson = !prev || prev.lessonIndex !== issue.lessonIndex;
                  const lessonForIssue = lessons[issue.lessonIndex];
                  return (
                    <React.Fragment key={issue.key}>
                      {newLesson && (
                        <IssueGroup>
                          {issue.lessonIndex + 1}. {fmtDate(lessonForIssue.date)}
                          {lessonForIssue.name ? ` — ${lessonForIssue.name}` : ''}
                        </IssueGroup>
                      )}
                      <IssueRow
                        type="button"
                        ref={(el) => { issueRowRefs.current[issue.key] = el; }}
                        $color={SEVERITY_META[issue.severity].color}
                        $active={!!activeIssue && activeIssue.key === issue.key}
                        onClick={() => goToIssue(issue)}
                        title={issue.hint || ''}
                      >
                        <IssueWhere>
                          {issue.blockId != null ? `блок #${issue.blockId} · ` : ''}{issue.title}
                        </IssueWhere>
                        <IssueMsg>{issue.message}</IssueMsg>
                        {issue.excerpt && <IssueExcerpt>{issue.excerpt}</IssueExcerpt>}
                      </IssueRow>
                    </React.Fragment>
                  );
                })}
              </IssueList>

              {issues.length > 0 && (
                <PanelFoot>
                  <NavBtn type="button" onClick={() => stepIssue(-1)}>↑ Пред.</NavBtn>
                  <NavBtn type="button" onClick={() => stepIssue(1)}>↓ След.</NavBtn>
                  <NavBtn type="button" onClick={copyIssuesReport}>Скопировать список</NavBtn>
                </PanelFoot>
              )}
            </Panel>
            )}
          </PreviewLayout>

          {selection && (
            <SelectionToolbar $top={selection.top} $left={selection.left}>
              <SelectBtn onClick={openCorrectionForm}>Отметить как ссылку</SelectBtn>
            </SelectionToolbar>
          )}

          {gapMenu && (
            <SelectionToolbar $top={gapMenu.top} $left={gapMenu.left}>
              <GapMenu>
                <SelectBtn onClick={markGapAsLink}>Отметить как ссылку</SelectBtn>
                <SelectBtn onClick={reportGap}>⚠ Сообщить</SelectBtn>
                <SelectBtn onClick={() => setGapMenu(null)} title="Закрыть">✕</SelectBtn>
              </GapMenu>
            </SelectionToolbar>
          )}

          {corrections.length > 0 && (
            <CorrectionsBar>
              <span>Правок ожидает применения: {corrections.length}</span>
              <ReapplyBtn disabled={reapplying} onClick={reapplyCorrections}>
                {reapplying ? 'Применяем…' : 'Повторно распознать'}
              </ReapplyBtn>
            </CorrectionsBar>
          )}

          {issueCounts.error > 0 && (
            <GateBar>
              <input
                type="checkbox"
                checked={forceUpload}
                onChange={e => setForceUpload(e.target.checked)}
              />
              Автопроверка нашла {issueCounts.error} {issueCounts.error === 1 ? 'ошибку' : 'ошибок'}. Отправлять всё равно — я проверил вручную.
            </GateBar>
          )}

          <SubmitBtn onClick={handleSubmit} disabled={uploading || (issueCounts.error > 0 && !forceUpload)}>
            {uploading ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                Загрузка…
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Отправить на сервер ({lessons.length} уроков)
              </>
            )}
          </SubmitBtn>
        </>
      )}

      {corrForm && (
        <CorrModal onClick={() => setCorrForm(null)}>
          <CorrCard onClick={e => e.stopPropagation()}>
            <CorrTitle>Отметить как библейскую ссылку</CorrTitle>
            <CorrFragment>{corrForm.text}</CorrFragment>

            <ModeRow>
              <ModeBtn type="button" active={corrForm.mode === 'auto'} onClick={() => setCorrForm(f => ({ ...f, mode: 'auto' }))}>
                Авто-распознавание
              </ModeBtn>
              <ModeBtn type="button" active={corrForm.mode === 'manual'} onClick={() => setCorrForm(f => ({ ...f, mode: 'manual' }))}>
                Указать вручную
              </ModeBtn>
            </ModeRow>

            {corrForm.mode === 'auto' ? (
              <GuideNote style={{ marginBottom: '1rem' }}>
                Фрагмент прогонится через ту же функцию распознавания, что и весь остальной текст —
                используйте, если ссылка написана в обычном цитатном формате («Кн. 3:7»), просто не была найдена.
              </GuideNote>
            ) : (
              <>
                {(corrForm.refs || []).map((ref, ri) => {
                  const setRef = (patch) => setCorrForm(f => ({
                    ...f,
                    refs: f.refs.map((r, i) => (i === ri ? { ...r, ...patch } : r)),
                  }));
                  const chapters = expandNumberList(ref.chapter);
                  const verses = expandNumberList(ref.verses);
                  return (
                    <RefCard key={ri}>
                      <RefHead>
                        <span>Отрывок {ri + 1}</span>
                        {corrForm.refs.length > 1 && (
                          <RefRemove
                            type="button"
                            title="Убрать отрывок"
                            onClick={() => setCorrForm(f => ({ ...f, refs: f.refs.filter((_, i) => i !== ri) }))}
                          >×</RefRemove>
                        )}
                      </RefHead>
                      <FieldRow>
                        <FieldLabel>Книга</FieldLabel>
                        <Select value={ref.bookNumber} onChange={e => setRef({ bookNumber: e.target.value })}>
                          <option value="">— выберите —</option>
                          {BOOK_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                        </Select>
                      </FieldRow>
                      <FieldRow>
                        <FieldLabel>Глава(ы)</FieldLabel>
                        <TextInput value={ref.chapter} onChange={e => setRef({ chapter: e.target.value })} placeholder="напр. 3 или 3-5" />
                      </FieldRow>
                      <FieldRow>
                        <FieldLabel>Стихи (необязательно)</FieldLabel>
                        <TextInput value={ref.verses} onChange={e => setRef({ verses: e.target.value })} placeholder="напр. 14-16, 18 — пусто = вся глава" />
                      </FieldRow>
                      <RefPreview>
                        {ref.bookNumber && chapters.length ? (
                          <>
                            Сохранится как: <b>{bookLabel(Number(ref.bookNumber))}</b>,{' '}
                            {chapters.length > 1 ? 'главы' : 'глава'} <b>{chapters.join(', ')}</b>,{' '}
                            {verses.length ? <>{verses.length > 1 ? 'стихи' : 'стих'} <b>{verses.join(', ')}</b></> : <b>вся глава</b>}
                            {chapters.length > 1 && verses.length > 0 && (
                              <>
                                <RefWarn>
                                  ⚠ Несколько глав вместе со стихами приложение показать не сможет: в модалке стихов
                                  читается только первая глава, а в заголовке номера глав слипаются. Это всегда разные
                                  отрывки — разделите их.
                                </RefWarn>
                                <SplitRefBtn
                                  type="button"
                                  onClick={() => setCorrForm(f => ({
                                    ...f,
                                    refs: f.refs.flatMap((r, i) => (i === ri ? splitRefByChapters(r) : [r])),
                                  }))}
                                >
                                  Разделить на {chapters.length} отрывка
                                </SplitRefBtn>
                              </>
                            )}
                          </>
                        ) : 'Заполните книгу и главу.'}
                      </RefPreview>
                    </RefCard>
                  );
                })}

                <AddRefBtn
                  type="button"
                  onClick={() => setCorrForm(f => ({
                    ...f,
                    // Новый отрывок обычно из той же книги — подставляем её,
                    // чтобы для «Рим. 15:4, 16:22» осталось вписать только главу.
                    refs: [...f.refs, { ...EMPTY_REF, bookNumber: f.refs[f.refs.length - 1]?.bookNumber || '' }],
                  }))}
                >+ Добавить отрывок (другая глава или книга)</AddRefBtn>

                <GuideNote style={{ margin: '0 0 0.75rem' }}>
                  Диапазоны можно писать через дефис — «14-16» развернётся в 14, 15, 16.
                </GuideNote>
              </>
            )}

            <CorrActions>
              <GhostBtn onClick={() => setCorrForm(null)}>Отмена</GhostBtn>
              <PrimaryBtn onClick={saveCorrection}>Сохранить правку</PrimaryBtn>
            </CorrActions>
          </CorrCard>
        </CorrModal>
      )}

      {reportTarget && (
        <CorrModal onClick={() => setReportTarget(null)}>
          <CorrCard onClick={e => e.stopPropagation()}>
            <CorrTitle>Сообщить о проблеме</CorrTitle>
            <CorrFragment>[{reportTarget.type}] {reportTarget.text}</CorrFragment>
            <FieldRow>
              <FieldLabel>Что не так (необязательно)</FieldLabel>
              <TextInput
                value={reportTarget.note}
                onChange={e => setReportTarget(t => ({ ...t, note: e.target.value }))}
                placeholder="напр. мусорные символы в конце текста"
              />
            </FieldRow>
            <GuideNote>Своего трекера пока нет — отчёт скопируется в буфер обмена, вставьте его в чат с разработчиком.</GuideNote>
            <CorrActions>
              <GhostBtn onClick={() => setReportTarget(null)}>Закрыть</GhostBtn>
              <PrimaryBtn onClick={() => { copyReport(); setReportTarget(null); }}>Скопировать отчёт</PrimaryBtn>
            </CorrActions>
          </CorrCard>
        </CorrModal>
      )}

      <ToastContainer position="bottom-right" />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Card>
  );
}

export default UploadTexts;
